import { Box, Typography } from '@mui/material'
import { memo } from 'react'
import type {
  TEnumItem,
  TPublisherLyricsApprovalDraftReviewSong,
  TPublisherSong,
} from '@modules/types'
import { getEnumNameById } from '@modules/api-core'
import {
  getCurrencySymbol,
  getTextColor,
} from '@screens/requests/components/PublisherRequest/common/feeDisplayUtils'
import { PriceDisplay } from './fee-quoted-price/components/PriceDisplay'
import { PriceInput } from './fee-quoted-price/components/PriceInput'
import { ActionButtons } from './fee-quoted-price/components/ActionButtons'

type LyricsDraftFeeFields = Pick<
  TPublisherLyricsApprovalDraftReviewSong,
  'lyricsPublisherFee' | 'counterOfferFee'
>

type LyricsCounterOfferInfo = {
  initialFee: number
  proposedFee: number
  currencyId: number
}

const getLyricsCounterOfferInfo = (song: TPublisherSong): LyricsCounterOfferInfo | null => {
  const fees = song.counterOfferFees
  if (!fees?.length) return null

  const counterOffer = fees[0]

  return {
    initialFee: counterOffer.initialFee,
    proposedFee: counterOffer.proposedFee,
    currencyId: counterOffer.currencyId,
  }
}

type LyricsFeeQuotedPriceProps = {
  song: TPublisherSong
  enums: TEnumItem[]
  initialLoading: boolean
  isLoading: boolean
  processingSongId: number | null
  isBlocked?: boolean
  songHasStatus: boolean
  draftReviewSong: LyricsDraftFeeFields
  isEditing: boolean
  feeValue: string
  onChange: (songId: number, value: string) => void
  onSave: (songId: number) => void
  onCancel: (songId: number) => void
  onEdit: (songId: number) => void
  validationError?: string
}

export const LyricsFeeQuotedPrice = memo(
  ({
    song,
    enums,
    initialLoading,
    isLoading,
    processingSongId,
    isBlocked,
    songHasStatus,
    draftReviewSong,
    isEditing,
    feeValue,
    onChange,
    onSave,
    onCancel,
    onEdit,
    validationError,
  }: LyricsFeeQuotedPriceProps) => {
    const textColor = getTextColor(songHasStatus, isLoading)
    const currencyName = getEnumNameById(
      enums,
      'request_currency',
      song.lyricsPublisherCurrency || 1,
    )
    const currency = getCurrencySymbol(currencyName)
    const displayPrice = feeValue
      ? Number(feeValue)
      : (draftReviewSong.lyricsPublisherFee ??
        draftReviewSong.counterOfferFee ??
        song.lyricsPublisherFee ??
        0)

    const counterOfferInfo = getLyricsCounterOfferInfo(song)

    return (
      <Box display="flex" px={3}>
        <Box
          display="flex"
          alignItems="flex-end"
          justifyContent="space-between"
          gap={3.5}
          sx={{ height: 50, color: textColor }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="caption" color={textColor}>
              Fee quoted price
            </Typography>

            {isEditing ? (
              <PriceInput
                value={feeValue}
                onChange={onChange}
                songId={song.id}
                initialLoading={initialLoading}
                currency={currency}
                isBlocked={isBlocked}
                validationError={validationError}
              />
            ) : (
              <PriceDisplay
                price={displayPrice}
                color={textColor}
                currency={currency}
                initialLoading={initialLoading}
                counterOfferInfo={counterOfferInfo}
                enums={enums}
                validationError={validationError}
              />
            )}
          </Box>

          <ActionButtons
            isEditing={isEditing}
            isLoading={isLoading}
            initialLoading={initialLoading}
            songId={song.id}
            onSave={(songId: number) => onSave(songId)}
            onCancel={(songId: number) => onCancel(songId)}
            onEdit={(songId: number) => onEdit(songId)}
            songHasStatus={songHasStatus}
            feeType="print"
            isBlocked={isBlocked}
            processingSongId={processingSongId}
            approvalDecision={song.approvalDecision}
          />
        </Box>
      </Box>
    )
  },
)
